// **React Imports
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

// **MUI Imports
import { Box, Button, Typography } from '@mui/material'

// **Component Imports
import CustomAvatar from '../avatar'
import Icon from '../icon'
import MCQQuestionRenderer from './MCQQuestionRenderer'

// **SCSS Import
import './index.scss'
import { QUESTION_SUB_TYPE_MAP } from '../../utils/constant'

// Custom component
const CustomTooltipContent = memo(() => {
  return (
    <Box
      sx={{
        gap: '16px',
        display: 'flex',
        flexDirection: 'column',
        padding: '16px'
      }}
    >
      <Typography variant="body2" sx={{ color: 'var(--common-white)' }}>
        Question Chips marked with <br />
        - Primary colour is currently selected, <br />
        - Green are correct, <br />
        - Red are incorrect, <br />
        - Gray are unattempted.
        <br />- Orange are partially correct.
      </Typography>
    </Box>
  )
})

const QuestionItem = memo(
  ({
    questionIndex,
    selectedQuestion,
    question,
    usedForEvaluation,
    handleQuestionChange,
    isPsychometric = false
  }) => {
    const isCurrentQuestion = useMemo(() => {
      return questionIndex === selectedQuestion
    }, [questionIndex, selectedQuestion])

    const { isAllCorrect, isPartialCorrect, isIncorrect } = useMemo(() => {
      if (question?.subtype === QUESTION_SUB_TYPE_MAP.COMPREHENSION) {
        let correctCount = 0
        let isAnyAttempted = false

        question?.subQuestions?.forEach((subQuestion) => {
          if (subQuestion?.score === subQuestion?.mark) {
            correctCount++
          }
          if (subQuestion?.score != null) {
            isAnyAttempted = true
          }
        })

        return {
          isAllCorrect: correctCount === question?.subQuestions?.length,
          isPartialCorrect:
            correctCount > 0 && correctCount < question?.subQuestions?.length,
          isIncorrect: isAnyAttempted && correctCount === 0
        }
      } else {
        const isAttempted = question?.score != null
        return {
          isAllCorrect: isPsychometric
            ? question?.isAttempted
            : isAttempted && question?.score === question?.mark,
          isPartialCorrect:
            isAttempted &&
            question?.score > 0 &&
            question?.score !== question?.mark,
          isIncorrect: isAttempted && question?.score <= 0
        }
      }
    }, [question, isPsychometric])

    return (
      <CustomAvatar
        color={
          isCurrentQuestion
            ? 'primary'
            : usedForEvaluation && isAllCorrect
              ? 'success'
              : usedForEvaluation && isPartialCorrect
                ? 'warning'
                : usedForEvaluation && isIncorrect
                  ? 'error'
                  : ''
        }
        className="question-item-container"
        sx={{
          mr: 1,
          width: 32,
          height: 32,
          fontWeight: 500,
          fontSize: '12px',
          cursor: 'pointer'
        }}
        onClick={() => handleQuestionChange(questionIndex)}
      >
        {questionIndex + 1}
      </CustomAvatar>
    )
  }
)

// ** Question list container right side container component
const QuestionListContainer = memo(
  ({
    questions,
    selectedQuestion,
    handleQuestionChange,
    usedForEvaluation = false,
    isPsychometric = false
  }) => {
    return (
      <Box className="question-list-container">
        <Typography className="title" variant="h5">
          Questions
          {usedForEvaluation && !isPsychometric && (
            <Icon
              title={<CustomTooltipContent />}
              icon="material-symbols:info-outline"
              className="icon"
              style={{ color: 'var(--text-secondary)' }}
            />
          )}
        </Typography>
        <Box className="question-container">
          {questions?.map((question, questionIndex) => (
            <QuestionItem
              question={question}
              questionIndex={questionIndex}
              selectedQuestion={selectedQuestion}
              usedForEvaluation={usedForEvaluation}
              handleQuestionChange={handleQuestionChange}
              isPsychometric={isPsychometric}
              key={`${question?.id || ''}-{questionIndex}`}
            />
          ))}
        </Box>
      </Box>
    )
  }
)

const MCQsPreviewer = ({
  allQuestions,
  addQuestionId,
  selectedFilter,
  setAddQuestionId,
  updateQuestionIndex,
  setUpdateQuestionIndex,
  needActionButton = false,
  actionButtonTitle = '',
  actionButtonHandler = () => {},
  usedForEvaluation = false,
  showActionButton = false,
  showQuestionMetaData = false,
  handleMetaDataUpdateClick = null,
  hasAccess,
  showStatus,
  publishPermission = false,
  refreshQuestionList = () => {},
  isPsychometric = false
}) => {
  // states
  const [selectedQuestion, setSelectedQuestion] = useState(0)

  // handler
  const handleQuestionChange = useCallback((questionIndex) => {
    setSelectedQuestion(questionIndex)
  }, [])

  const handleQuestionIncrementDecrement = useCallback(
    (increment = true) => {
      setSelectedQuestion((prev) => {
        if (increment && prev < allQuestions?.length - 1) {
          return prev + 1
        } else if (!increment && prev > 0) {
          return prev - 1
        } else {
          return prev
        }
      })
    },
    [allQuestions]
  )

  //useEffect
  useEffect(() => {
    handleQuestionChange(0)
  }, [selectedFilter, handleQuestionChange])

  useEffect(() => {
    if (updateQuestionIndex !== null && updateQuestionIndex !== undefined) {
      setUpdateQuestionIndex(null)
      handleQuestionChange(updateQuestionIndex)
    }
  }, [updateQuestionIndex, handleQuestionChange, setUpdateQuestionIndex])

  useEffect(() => {
    if (allQuestions?.length > 0 && addQuestionId) {
      const index = allQuestions.findIndex((q) => q?.id === addQuestionId)
      handleQuestionChange(index !== -1 ? index : 0)
      setAddQuestionId(null)
    }
  }, [addQuestionId, allQuestions, handleQuestionChange, setAddQuestionId])

  return (
    <Box className="mcqs-question-main-container">
      <Box className="mcqs-question-list-container">
        <Box
          className="mcqs-container"
          sx={{
            ...(selectedFilter === 'timeline'
              ? { width: '100% !important' }
              : {})
          }}
        >
          <Box className="mcq-inner-container">
            <MCQQuestionRenderer
              question={allQuestions?.[selectedQuestion] || null}
              questionNo={selectedQuestion + 1}
              needActionButton={needActionButton}
              actionButtonTitle={actionButtonTitle}
              actionButtonHandler={actionButtonHandler}
              usedForEvaluation={usedForEvaluation}
              showActionButton={showActionButton}
              hasAccess={hasAccess}
              showQuestionMetaData={showQuestionMetaData}
              handleMetaDataUpdateClick={handleMetaDataUpdateClick}
              showStatus={showStatus}
              publishPermission={publishPermission}
              refreshQuestionList={refreshQuestionList}
              isPsychometric={isPsychometric}
            />
            {allQuestions?.length > 0 && (
              <Box className="mcq-controls-container">
                {selectedQuestion !== 0 ? (
                  <Button
                    className="btn"
                    variant="contained"
                    color="error"
                    startIcon={<Icon icon={'ion:chevron-back-sharp'} />}
                    onClick={() => handleQuestionIncrementDecrement(false)}
                  >
                    Previous Question
                  </Button>
                ) : (
                  <Box />
                )}
                {selectedQuestion !== (allQuestions?.length - 1 || 0) ? (
                  <Button
                    className="btn"
                    variant="contained"
                    color="success"
                    endIcon={<Icon icon={'akar-icons:chevron-right'} />}
                    onClick={() => handleQuestionIncrementDecrement()}
                  >
                    Next Question
                  </Button>
                ) : (
                  <Box />
                )}
              </Box>
            )}
          </Box>
        </Box>

        {selectedFilter !== 'timeline' && (
          <>
            <Box className="resizer" id="resizer" />
            <QuestionListContainer
              questions={allQuestions || []}
              selectedQuestion={selectedQuestion}
              handleQuestionChange={handleQuestionChange}
              usedForEvaluation={usedForEvaluation}
              isPsychometric={isPsychometric}
            />
          </>
        )}
      </Box>
    </Box>
  )
}

export default memo(MCQsPreviewer)
